/**
 * Segmentation rules — single source of truth for frontend.
 * Keep in sync with backend/utils/segmentation.py
 * Last updated: 2026-05-04
 */
export const SEGMENTATION_RULES = {
  splitThreshold: 40,
  minSentenceWords: 3,
  maxSentenceWords: 120,
  chunkSize: 60,
  numericThreshold: 0.4,
  sentenceRegex: /(?<=[.!?।])\s+/,
  normalizeMissingSpace: /([.!?])([A-Z\u0900-\u097F])/g
};

export function splitIntoSentences(text) {
  if (!text || !text.trim()) return [text];
  const normalized = text.replace(SEGMENTATION_RULES.normalizeMissingSpace, '$1 $2');
  const sentences = normalized.split(SEGMENTATION_RULES.sentenceRegex);
  const result = sentences.map(s => s.trim()).filter(s => s);
  return result.length > 0 ? result : [text.trim()];
}

export function mergeShortSentences(sentences) {
  const result = [];
  let buffer = '';
  for (const s of sentences) {
    if (s.split(/\s+/).length < SEGMENTATION_RULES.minSentenceWords) {
      buffer += ' ' + s;
    } else {
      if (buffer) { result.push(buffer.trim()); buffer = ''; }
      result.push(s);
    }
  }
  if (buffer) result.push(buffer.trim());
  return result.length > 0 ? result : sentences;
}

export function chunkLongSentence(sentence) {
  const words = sentence.split(/\s+/);
  if (words.length <= SEGMENTATION_RULES.maxSentenceWords) return [sentence];
  const chunks = [];
  for (let i = 0; i < words.length; i += SEGMENTATION_RULES.chunkSize) {
    chunks.push(words.slice(i, i + SEGMENTATION_RULES.chunkSize).join(' '));
  }
  return chunks;
}

export function shouldSplitParagraph(text) {
  if (!text) return false;
  if (text.split(/\s+/).length <= SEGMENTATION_RULES.splitThreshold) return false;
  const normalized = text.replace(SEGMENTATION_RULES.normalizeMissingSpace, '$1 $2');
  return /[.!?।]\s+/.test(normalized);
}

export function isNumericHeavy(text) {
  if (!text) return false;
  const digits = (text.match(/\d/g) || []).length;
  return digits / Math.max(text.length, 1) > SEGMENTATION_RULES.numericThreshold;
}

export function segmentText(text) {
  if (!text || !text.trim()) return [text];
  if (isNumericHeavy(text)) return [text];
  if (!shouldSplitParagraph(text)) return [text];
  let sentences = splitIntoSentences(text);
  if (sentences.length <= 1) return [text];
  sentences = mergeShortSentences(sentences);
  const final = [];
  for (const s of sentences) final.push(...chunkLongSentence(s));
  const filtered = final.filter(s => s.trim());
  return filtered.length > 0 ? filtered : [text];
}
